import styled from "styled-components";
import css from "@styled-system/css";
import {
  position,
  PositionProps,
  flexbox,
  FlexboxProps,
  border,
  BorderProps,
} from "styled-system";

import { BannerProps } from "./banner.type";
import variants from "./variants";
import Box from "../Box/Box";
import Button from "../Button/Button";
import LinkButton from "../Button/LinkButton";
import { ButtonProps, LinkButtonProps } from "../Button/button.type";
import Typography from "../Typography/Typography";
import Icon from "../Icon/Icon";

const PositioningContainer = styled(Box)<PositionProps>`
  ${position}
`;

const BannerContainer = styled(Box)<BorderProps & { roundedCorners: boolean }>`
  border-style: solid;
  ${(props) =>
    css({
      borderWidth: 0,
      borderLeftWidth: "border-size-bold",
      borderRadius: props.roundedCorners ? 1 : 0,
    })}
  ${border}
`;

const ActionLink = styled(LinkButton)<LinkButtonProps>`
  cursor: pointer;
  text-decoration: underline;
  ${css({
    fontWeight: "normal",
    marginRight: 5,
  })};
`;

const ActionButton = styled(Button)<ButtonProps>`
  cursor: pointer;
  ${css({
    fontWeight: "normal",
    marginRight: 5,
  })};
`;

const HeadlineTitle = styled(Typography)<FlexboxProps>`
  ${flexbox}
  ${css({
    fontWeight: "semiBold",
    fontSize: 4,
  })};
`;

const DialogDescription = styled(Box)`
  ${css({
    marginTop: 2,
    fontSize: 2,
    color: "grey-blue",
  })};
  & a {
    cursor: pointer;
    ${css({
      color: "cta-tertiary-text-color-default",
    })};
    &:hover,
    &:focus {
      ${css({
        color: "cta-tertiary-text-color-hover",
      })};
    }
  }
`;

const Banner = ({
  id,
  title,
  children,
  additionalInfo,
  variant = "info",
  iconName,
  iconWeight = "solid",
  displayMode = "overlay",
  paddingX = 0,
  paddingY = 0,
  primaryLinkType = "button",
  primaryLinkLabel,
  primaryLinkAction,
  primaryLinkUrl,
  primaryLinkOpenInNewTab = false,
  secondaryLinkType = "button",
  secondaryLinkLabel,
  secondaryLinkAction,
  secondaryLinkUrl,
  secondaryLinkOpenInNewTab = false,
  primaryLinkAnalytics,
  secondaryLinkAnalytics,
  roundedCorners = true,
  titleColor,
  ...props
}: BannerProps) => {
  const hasActionLinks = primaryLinkLabel || secondaryLinkLabel;
  const position = displayMode === "inline" ? "relative" : "fixed";
  return (
    <PositioningContainer
      display="flex"
      justifyItems="center"
      paddingX={paddingX}
      paddingY={paddingY}
      width="100%"
      height="auto"
      role="alert"
      aria-labelledby="dialogTitle"
      aria-describedby="dialogDescription"
      position={position}
      top={0}
      right={0}
      zIndex={1000}
      {...props}
    >
      <BannerContainer
        id={id}
        width="100%"
        height="auto"
        backgroundColor={variants[variant]?.backgroundColor}
        borderColor={variants[variant]?.borderColor}
        display="flex"
        paddingTop={7}
        paddingBottom={6}
        paddingLeft={7}
        paddingRight={8}
        roundedCorners={roundedCorners}
      >
        <Icon
          name={iconName || variants[variant]?.iconName}
          weight={iconWeight || variants[variant]?.weight}
          width={30}
          height={30}
          color={variants[variant]?.iconColor}
          aria-hidden="true"
        />
        <Box pl={6} flex={1}>
          <HeadlineTitle
            flex={1}
            marginY={0}
            variant="body"
            htmlTag="h6"
            color={titleColor ? titleColor : variants[variant]?.titleColor}
            id="dialogTitle"
          >
            {title}
          </HeadlineTitle>
          {children && (
            <DialogDescription id="dialogDescription">
              {children}
            </DialogDescription>
          )}
          {hasActionLinks && (
            <Box mt={6}>
              {primaryLinkLabel && primaryLinkType === "link" && (
                <ActionLink
                  link={primaryLinkUrl}
                  shouldOpenInNewWindow={primaryLinkOpenInNewTab}
                  variant="tertiary"
                  data-en="click"
                  data-ec="banner-link-primary"
                  data-el={primaryLinkAnalytics?.eventLabel}
                  data-ea={primaryLinkAnalytics?.eventAction}
                  data-es={primaryLinkAnalytics?.eventSection}
                  data-et={primaryLinkAnalytics?.eventType}
                  data-ev={primaryLinkAnalytics?.eventValue}
                  height={14}
                  text={primaryLinkLabel}
                  p={0}
                />
              )}
              {primaryLinkLabel && primaryLinkType === "button" && (
                <ActionButton
                  onClick={primaryLinkAction}
                  variant="tertiary"
                  data-en="click"
                  data-ec="banner-link-primary"
                  data-el={primaryLinkAnalytics?.eventLabel}
                  data-ea={primaryLinkAnalytics?.eventAction}
                  data-es={primaryLinkAnalytics?.eventSection}
                  data-et={primaryLinkAnalytics?.eventType}
                  data-ev={primaryLinkAnalytics?.eventValue}
                  height={14}
                  text={primaryLinkLabel}
                  p={0}
                />
              )}
              {secondaryLinkLabel && secondaryLinkType === "link" && (
                <ActionLink
                  link={secondaryLinkUrl}
                  shouldOpenInNewWindow={secondaryLinkOpenInNewTab}
                  variant="tertiary"
                  data-en="click"
                  data-ec="banner-link-secondary"
                  data-el={secondaryLinkAnalytics?.eventLabel}
                  data-ea={secondaryLinkAnalytics?.eventAction}
                  data-es={secondaryLinkAnalytics?.eventSection}
                  data-et={secondaryLinkAnalytics?.eventType}
                  data-ev={secondaryLinkAnalytics?.eventValue}
                  height={14}
                  text={secondaryLinkLabel}
                  p={0}
                />
              )}
              {secondaryLinkLabel && secondaryLinkType === "button" && (
                <ActionButton
                  onClick={secondaryLinkAction}
                  variant="tertiary"
                  data-en="click"
                  data-ec="banner-link-secondary"
                  data-el={secondaryLinkAnalytics?.eventLabel}
                  data-ea={secondaryLinkAnalytics?.eventAction}
                  data-es={secondaryLinkAnalytics?.eventSection}
                  data-et={secondaryLinkAnalytics?.eventType}
                  data-ev={secondaryLinkAnalytics?.eventValue}
                  height={14}
                  text={secondaryLinkLabel}
                  p={0}
                />
              )}
            </Box>
          )}
          {additionalInfo && (
            <Box
              display={{ _: "none", tablet: "block" }}
              mt={hasActionLinks ? 6 : 3}
              color="informative-text-color"
            >
              {additionalInfo}
            </Box>
          )}
        </Box>
      </BannerContainer>
    </PositioningContainer>
  );
};

export default Banner;
